// @ts-strict-ignore
import { areEqual } from 'flow/service/util';
import type { WorkflowState } from '../../store/redux/state';
import type { Comparable } from './types';
import { Operation } from './types';
import type { PublishedAttribute } from 'flow/store/engine/types';
import { REGEX as ACTION_REGEX } from '../actions/constants';
import {
  getFirstSnippetParentId,
  getGateRuleThresholdId,
} from '../actions/snippets/snippets';


export const REGEX = {
  ACTION_ROOT_PATH: /^\/actions\/action__\w*__\w{20}$/,
  ACTION_PATH_TO_NESTED_FIELDS: /^\/actions\/action__\w*__\w{20}/,
  ACTION_PUBLIC_ID: ACTION_REGEX.ACTION_PUBLIC_ID,
  INPUT_ACTION_PUBLIC_ID: ACTION_REGEX.INPUT_ACTION_PUBLIC_ID,
  OUTPUT_ACTION_PUBLIC_ID: ACTION_REGEX.OUTPUT_ACTION_PUBLIC_ID,
  TAG_ACTION_PUBLIC_ID: ACTION_REGEX.TAG_ACTION_PUBLIC_ID,
  OUTCOME_ACTION_PUBLIC_ID: ACTION_REGEX.OUTCOME_ACTION_PUBLIC_ID,
  WORKFLOW_ROUTING_ACTION_PUBLIC_ID: ACTION_REGEX.WORKFLOW_ROUTING_ACTION_PUBLIC_ID,
  OUTPUT_ACTION_EXPRESSION_ROOT: /^\/actions\/action__output__\w{20}\/output\/expression/,
  OUTPUT_ACTION_OPTIONS_ROOT: /^\/actions\/action__output__\w{20}\/options/,
  MATRIX_MODEL_ACTION_METADATA_ROOT: /^\/actions\/action__matrix_model__\w{20}\/matrixModel/,
  MATRIX_MODEL_ACTION_OPTIONS_ROOT: /^\/actions\/action__matrix_model__\w{20}\/options/,
  MATRIX_MODEL_DEFINE_VALUE_RANGES: /^\/actions\/action__matrix_model__\w{20}\/matrixModel\/defineValueRanges/,
  ACTION_SERVICE_FAILURE_DEFAULT: /^\/actions\/action__\w*__\w{20}\/serviceFailureDefault$/,
  /**
   * Matches any path to an action rule, regardless of how nested it is. For example:
   * /actions/:publicId/rules
   * /actions/:publicId/rules/and/1/id
   * /actions/:publicId/rules/and/1/and/2/id
   *
   * The ?: tells the parser not to create a capture group for the contents following it.
   * This is simply so that running a `string.match(regex)` returns a single value.
   */
  ACTION_RULE_CONTENT:
    /^\/actions\/action__\w*__\w{20}\/rules\/(?:id|and|or)(?:\/(?:id|or|and|\d+))*/,
  /**
    *  Matches a path to a value within an MLA. for example:
    * /thresholds/threshold__6fc9a737cc2ad22792f1/left/attributeComponents/2/value
    */
  ACTION_RULE_MLA_CONTENT: /^\/thresholds\/threshold__\w{20}\/(?:left|right)\/attributeComponents/,
  TAG_ACTION_GLOBAL_SETTINGS: /^\/actions\/action__tag__\w{20}\/tag\//,
  SERVICE_ACTION_OPTIONS_ROOT: /^\/actions\/action__service__\w{20}\/options/,
  SERVICE_CREDENTIAL_OWNER: /^\/actions\/action__service__\w{20}\/service\/credentialOwner/,
  THRESHOLD_ROOT_PATH: /^\/thresholds\/threshold__\w{20}$/,
  THRESHOLD_PUBLIC_ID: /threshold__\w{20}/,
  // Data Routes
  DATAROUTE_ROOT_PATH: /^\/dataRoutes\/dataroute__\w{20}$/,
  DATAROUTE_PUBLIC_ID: /dataroute__\w{20}/,
  DATAROUTE_MLA_CONTENT: /^\/dataRoutes\/dataroute__\w{20}\/attribute\/multiLevelAttributeFilter\/filterComponents\//,
  // Matches a path to an outcome within a tag - /actions/:publicId/tag/outcomes/:number
  ACTION_TAG_GLOBAL_SETTINGS_OUTCOMES: /^\/actions\/action__tag__\w{20}\/tag\/outcomes\/\d+$/,
  PUBLISHED_ATTRIBUTE_PATH_TO_FIELDS: /^\/publishedAttributes\/\d+\/\w+$/,
  THRESHOLD_LEFT_RIGHT_OR_OPERATOR_PATH: /^\/thresholds\/threshold__\w{20}\/(?:left|operator|right)/,
  // SDK
  SDK_CONFIG_PLUGIN: /^\/sdkConfig\/plugin$/,
  SDK_CONFIG_VALUE: /^\/sdkConfig\/fields\/\d+\/value.*/,
  // UTIL
  GRAB_NUMBER: /(\d+)/,
};

/**
 * This function sorts a target PA array using the reference PA array to ensure the same
 * order of PAs in both comparable objects. It returns a new array with existing PAs at
 * the beginning and new ones at the end. This maintains consistency in the order of existing
 * PAs and ensures new ones appear at the end
 */
export function sortPublishedAttributesByExistence(options: {
  referencePublishedAttributes: PublishedAttribute[];
  targetPublishedAttributes: PublishedAttribute[];
}): PublishedAttribute[] {
  const {
    targetPublishedAttributes,
    referencePublishedAttributes,
  } = options;

  if (referencePublishedAttributes === targetPublishedAttributes) {
    return targetPublishedAttributes;
  }

  const referencePATokens = new Set(referencePublishedAttributes.map(pa => pa.attributeToken));
  const targetPAsPresentInReference = targetPublishedAttributes
    .filter(pa => referencePATokens.has(pa.attributeToken));

  const targetPAsNotPresentInReference = targetPublishedAttributes
    .filter(pa => !referencePATokens.has(pa.attributeToken));

  // put the PAs present in the reference at the beginning, and the new ones at the end
  return [
    ...targetPAsPresentInReference,
    ...targetPAsNotPresentInReference,
  ];
}

/**
 * builds an object that contains the fields that are supported in the changelog
 */
export function buildComparable(args: {
  compareInput: {
    version: WorkflowState['workflowData'];
    workflowProps: WorkflowState['workflowProps'];
  };
  supplementalData: {
    initialWorkflowProps: WorkflowState['workflowProps'];
  };
}): Comparable {
  const {
    compareInput: {
      version,
      workflowProps,
    },
    supplementalData: {
      initialWorkflowProps,
    },
  } = args;

  return {
    actions: version?.actions,
    dataRoutes: version?.dataRoutes,
    thresholds: version?.thresholds,
    sdkConfig: version?.sdkConfig,
    publishedAttributes: sortPublishedAttributesByExistence({
      referencePublishedAttributes: initialWorkflowProps?.publishedAttributes,
      targetPublishedAttributes: workflowProps?.publishedAttributes,
    }),
    failureHandlingMode: version?.failureHandlingMode,
  };
}

/**
 * Grabs the action publicId of a string if it exists.
 * @param s string possibly containing an action publicId
 * @returns the publicId within `s`
 */
export function extractActionPublicId(s: string): string {
  // foo/action__outcome__20charhash matches to `[action__outcome__20charhash]`
  const matches = s.match(REGEX.ACTION_PUBLIC_ID);
  if (!matches) {
    return null;
  }

  return matches[0];
}

export function extractThresholdPublicId(s: string): string {
  // foo/threshold__20charshash matches to `[threshold_20charhash]`
  const matches = s.match(REGEX.THRESHOLD_PUBLIC_ID);
  if (!matches) {
    return null;
  }

  return matches[0];
}

export function extractDataRoutePublicId(s: string): string {
  const matches = s.match(REGEX.DATAROUTE_PUBLIC_ID);
  if (!matches) {
    return null;
  }
  return matches[0];
}


/**
 * Compares two values and determines the Operation that happened between them
 * Returns `null` if the values are the same (no Operation happened)
 */
export function determineOperationForField(prevFieldValue: any, nextFieldValue: any): Operation {
  // add
  if (!prevFieldValue && nextFieldValue) {
    return Operation.Add;
  }

  // modified
  if ((prevFieldValue && nextFieldValue)
    && !areEqual(prevFieldValue, nextFieldValue)) {
    return Operation.Modify;
  }

  // remove
  if (prevFieldValue && !nextFieldValue) {
    return Operation.Remove;
  }

  // noop
  return null;
}

/**
 * Compares two values and determines the Operation that happened between them
 * Returns `null` if the values are the same (no Operation happened)
 */
export function determineOperationForCredentialOwner(
  prevFieldValue: any, nextFieldValue: any, prevOwnerField: any, nextOwnerField: any,
): Operation {
  // add
  if (!prevOwnerField && nextOwnerField) {
    return Operation.Add;
  }

  // modified
  if ((prevOwnerField && nextOwnerField)
    && !areEqual(prevFieldValue, nextFieldValue)) {
    return Operation.Modify;
  }

  // remove
  if (prevOwnerField && !nextOwnerField) {
    return Operation.Remove;
  }

  // noop
  return null;
}

/**
 * Returns a comparable with gate rule thresholds omitted so they are excluded from the changelog diff.
 */
export function omitGateRuleThresholds(comparable: Comparable): Comparable {
  const actions = comparable?.actions ?? {};
  const thresholds = comparable?.thresholds ?? {};
  const actionValues = Object.values(actions);
  const filteredThresholds = { ...thresholds };
  for (const action of actionValues) {
    const snippetParentActionId = getFirstSnippetParentId(action.containerContext);
    if (snippetParentActionId != null) {
      const id = getGateRuleThresholdId({
        snippetChildActionId: action.publicId,
        snippetParentActionId,
        thresholds,
      });
      if (id != null) {
        delete filteredThresholds[id];
      }
    }
  }
  return {
    ...comparable,
    thresholds: filteredThresholds,
  };
}
